/**
 * Which unfinished session, if any, a person should be taken back into.
 *
 * One rule, used in two places so they cannot disagree:
 *   - Home shows a "session in progress" card for it.
 *   - `/session` resumes it when the browser brings someone there without
 *     saying what they want (forward button, reload of a fresh entry, a
 *     bookmark, a typed address). See #46.
 */

/** The fields the rule needs. Any wider session row satisfies this. */
export type ResumableFields = {
  id: string
  is_complete: boolean | null
  phase_reached: number | null
}

/** How many recent sessions are considered. Matches what Home loads. */
export const RESUME_LOOKBACK = 10

/**
 * The most recent session that was started and not finished.
 *
 * `phase_reached > 0` means at least the first step was completed, so there is
 * saved work to return to. A row at 0 was opened and abandoned before anything
 * was written and is not worth resuming.
 *
 * @param sessions  Recent sessions, newest first.
 */
export function pickResumable<T extends ResumableFields>(sessions: readonly T[]): T | undefined {
  return sessions.find(s => !s.is_complete && (s.phase_reached ?? 0) > 0)
}

/**
 * What `/session` was opened with, read from react-router's navigation state.
 * Every field is optional: an empty state means the browser, not a button in
 * the app, brought the person here.
 */
export type SessionEntryState = {
  /** Resume this session. Set by Home's card, and by the page itself once a session exists. */
  resumeSession?: { id: string; phaseReached?: number }
  /** A deliberate new session ("Begin a session"). Never resumes. */
  fresh?: boolean
  /** "Explore this further" from the Close step: a new session with this emotion preselected. */
  preselectedEmotion?: string
  /** A guided-series session launched from the Journal. Typed where it is consumed. */
  seriesLaunch?: unknown
  /** Marks the spare history entry kept by `useBrowserBack`. */
  backGuard?: boolean
}

export type SessionEntry =
  | { kind: 'resume'; id: string }
  | { kind: 'fresh' }
  | { kind: 'lookup' }

/**
 * Decide what opening `/session` means.
 *
 * - `resume`: a specific session was asked for.
 * - `fresh`:  the person chose to start something new; do not resume.
 * - `lookup`: nothing was said, so check for an unfinished session and resume
 *             it if there is one. This is the forward / reload / bookmark case.
 */
export function resolveSessionEntry(state: SessionEntryState | null | undefined): SessionEntry {
  if (state?.resumeSession?.id) return { kind: 'resume', id: state.resumeSession.id }
  if (state?.fresh || state?.preselectedEmotion || state?.seriesLaunch) return { kind: 'fresh' }
  return { kind: 'lookup' }
}
